import { DndContext } from '@dnd-kit/core';
import { Box, Flex } from '@osmosis/react-ui/components/Box';
import React, { useEffect, useMemo, useState } from 'react';
import { useQuizContext } from '../../../../context/QuizContext';
import {
  SentenceDragAndDropData,
  SentenceDragAndDropSubmittedAnswerType,
} from '../../../../types/quiz';
import { P } from '@osmosis/react-ui/components/Text';
import { QuizContainer } from '../../QuizContainer';
import QuizSubmitButtons from '../../QuizSubmitButtons';
import { SentenceDraggableOverlay } from './SentenceDraggableOverlay';
import { SentenceDraggableItem } from './SentenceDraggableItem';
import { SentenceDroppableContainer } from './SentenceDroppableContainer';
import { CorrectBanner, SkipQuizButton } from '../utils';
import { SentenceDnDResetButton } from './SentenceDnDResetButton';
import { isCorrectAnswer, setData } from './utils';

/**
 * SentenceDragAndDrop.
 */
const SentenceDragAndDrop = () => {
  const { submitAnswer, currentResponseItem, currentItem, isAnswered } =
    useQuizContext();
  const data = currentItem?.data as SentenceDragAndDropData;
  const { answers, questions, initialState } = useMemo(
    () => setData(data.answers, data.distractors),
    [data],
  );

  const [fields, setFields] =
    useState<SentenceDragAndDropSubmittedAnswerType>(initialState);

  const fieldsResponse = currentResponseItem?.response
    ?.answer as SentenceDragAndDropSubmittedAnswerType;

  const currentFields = fieldsResponse || fields;

  useEffect(() => {
    setFields(initialState);
  }, [initialState]);

  return (
    <Box>
      <QuizContainer>
        <DndContext
          onDragEnd={({ active, over }) => {
            setFields((iteratedParent) => {
              const question = Object.keys(iteratedParent).reduce(
                (sum, curr) => {
                  if (fields[curr] === active.id) {
                    return { ...sum, [curr]: null };
                  }
                  return { ...sum, [curr]: fields[curr] };
                },
                {},
              );

              return over ? { ...question, [over.id]: active.id } : question;
            });
          }}
        >
          <SentenceDraggableOverlay allQuestions={questions} />
          <Box>
            {answers.map((answer) => {
              if (answer.type === 'text') {
                return (
                  <P
                    sx={{ display: 'inline' }}
                    key={answer.id}
                    dangerouslySetInnerHTML={{ __html: answer.value }}
                  />
                );
              }
              const dataForField = questions.find(
                (question) => question.id === currentFields[answer.parentId],
              );

              return (
                <SentenceDroppableContainer
                  key={answer.parentId}
                  id={answer.parentId}
                  allQuestions={questions}
                >
                  {dataForField && (
                    <SentenceDraggableItem
                      value={dataForField.value}
                      id={dataForField.id}
                      isCorrect={
                        answer.parentId.replace('_parent', '') ===
                        dataForField.id
                      }
                    />
                  )}
                </SentenceDroppableContainer>
              );
            })}
          </Box>
          <Flex mt={4} flexWrap="wrap" sx={{ gap: 2 }}>
            {questions.map((question) => {
              const fieldsArray = Object.values(fields);

              if (fieldsArray.includes(question.id)) {
                return null;
              }

              return (
                <SentenceDraggableItem
                  key={question.id}
                  value={question.value}
                  id={question.id}
                />
              );
            })}
          </Flex>
        </DndContext>

        <SentenceDnDResetButton
          onReset={() => setFields(initialState)}
          isShow={
            Object.values(fields).some((value) => Boolean(value)) && !isAnswered
          }
        />

        {isAnswered && (
          <CorrectBanner isCorrectAnswer={isCorrectAnswer(fields)} />
        )}
      </QuizContainer>

      {Object.values(fields).every((value) => Boolean(value)) &&
        !isAnswered && (
          <QuizSubmitButtons
            submit={(c) => submitAnswer(c, isCorrectAnswer(fields), fields)}
          />
        )}
      <SkipQuizButton />
    </Box>
  );
};

export default SentenceDragAndDrop;
